import { Link, useNavigate } from 'react-router-dom';
import { useAuth, SignedIn, SignedOut } from '@clerk/clerk-react';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  AvatarGroup,
  Button,
  Grid,
} from '@mui/material';
import { Add as AddIcon, GroupWork as GroupIcon } from '@mui/icons-material';
import { AUTH_ENDPOINTS } from '../../config/api';
import type { GroupData } from '../../types/api.types';
import { useUserData } from '../../hooks/useUserData';
import { useUserSubscriptions } from '../../hooks/useUserSubscriptions';

interface GroupCardProps {
  group: GroupData;
  channelThumbnails: string[];
  channelCount: number;
}

const GroupCard = ({ group, channelThumbnails, channelCount }: GroupCardProps) => {
  return (
    group.id && (
      <Card
        sx={{
          height: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
      >
        <Link to={`/watch/${encodeURIComponent(group.id)}`}>
          <CardActionArea sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography
                  variant="h3"
                  component="h3"
                  sx={{
                    fontWeight: 600,
                    flexGrow: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {group.name}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                {channelThumbnails.length > 0 ? (
                  <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                    {channelThumbnails.map((thumbnail, index) => (
                      <Avatar
                        key={index}
                        src={thumbnail}
                        sx={{
                          width: 40,
                          height: 40,
                          border: '2px solid',
                          borderColor: 'background.paper',
                        }}
                      />
                    ))}
                  </AvatarGroup>
                ) : (
                  <Box
                    sx={{
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body2">No channels in this group</Typography>
                  </Box>
                )}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 2, marginLeft: 'auto' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {channelCount} channel{channelCount !== 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                    View Videos →
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </CardActionArea>
        </Link>
      </Card>
    )
  );
};

export const AppPage = () => {
  const { isLoaded } = useAuth();
  const navigate = useNavigate();
  const {
    data: userData,
    error: userDataError,
    isLoading: isUserDataLoading,
  } = useUserData();
  const {
    subscriptions,
    error: subscriptionsError,
    isLoading: isSubscriptionsLoading,
  } = useUserSubscriptions();

  const loading = !isLoaded || isUserDataLoading || isSubscriptionsLoading;
  const error = userDataError?.message ?? subscriptionsError?.message ?? null;

  // Build channel map for quick thumbnail lookup
  const channelMap = subscriptions.reduce(
    (map, sub) => {
      map[sub.channelId] = sub.channel;
      return map;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {} as Record<string, any>,
  );

  // Handle create group click
  const handleCreateGroup = () => {
    navigate('/groups');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading your groups...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <SignedOut>
        <Alert severity="error" sx={{ mb: 3 }}>
          Please sign in to access your groups.
        </Alert>
      </SignedOut>

      <SignedIn>
        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            {error.includes('sign in') && (
              <Box mt={2}>
                <Button variant="contained" color="primary" href={AUTH_ENDPOINTS.GOOGLE_LOGIN}>
                  Connect YouTube
                </Button>
              </Box>
            )}
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
                My Groups
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Organize and access your YouTube subscriptions
              </Typography>
            </Box>

            {userData?.groups && userData.groups.length > 0 ? (
              <>
                <Grid container spacing={3}>
                  {userData.groups.map(group => {
                    const groupChannels = group.channelIds.map(id => channelMap[id]).filter(Boolean);
                    const thumbnails = groupChannels.slice(0, 3).map(channel => channel.thumbnailUrl);

                    return (
                      <Grid key={group.name} size={{ xs: 12, sm: 6, md: 4 }}>
                        <GroupCard
                          group={group}
                          channelThumbnails={thumbnails}
                          channelCount={group.channelIds.length}
                        />
                      </Grid>
                    );
                  })}

                  {/* Add new group card */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card
                      sx={{
                        height: '100%',
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <CardActionArea onClick={handleCreateGroup} sx={{ height: '100%' }}>
                        <CardContent
                          sx={{
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <AddIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Create New Group
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Organize more channels
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                </Grid>
              </>
            ) : (
              // No groups exist - show empty state
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  No Groups Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                  Create your first group to start organizing your YouTube subscriptions into manageable categories.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleCreateGroup}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Create Your First Group
                </Button>
              </Box>
            )}
          </>
        )}
      </SignedIn>
    </Container>
  );
};
